import { Box, Flex, Image, Text } from '@chakra-ui/react'
import { Image as ImageIcon } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { emojiParser } from '~/constants/parsers'
import {
  GradientBackgroundSendingAnimation,
  PendingMessageBubbleVariants,
  usePendingMessageBubbleStyles,
} from '~/pages/Messaging/componentStyles/PendingMessageBubble.style'
import { PendingMessage } from '~/pages/Messaging/hooks/usePendingMessages'
import { fileToBase64 } from '~/utils/formatter'

interface PendingMessageBubbleProps {
  message: PendingMessage
  onRetry?: (message: PendingMessage) => void
}
const PendingMessageBubble = ({
  message,
  onRetry,
}: PendingMessageBubbleProps) => {
  const { t } = useTranslation()
  // Count of emojis for the current message.
  const { enlargeEmojies } = emojiParser(message.body)
  const [fileUrl, setFileUrl] = useState<string | undefined>(undefined)

  const isFailed = message.status === 'failed'
  const canRetry = message.originalSendParams !== undefined
  const hasBody = message.body && message.body !== ''
  const isEnlarged =
    enlargeEmojies || (message.messageType === 'file' && !hasBody)

  let variant: PendingMessageBubbleVariants
  if (isFailed) {
    variant = isEnlarged
      ? PendingMessageBubbleVariants.FailedEnlarged
      : PendingMessageBubbleVariants.Failed
  } else {
    variant = isEnlarged
      ? PendingMessageBubbleVariants.SendingEnlarged
      : PendingMessageBubbleVariants.Sending
  }

  const styles = usePendingMessageBubbleStyles({
    variant: variant,
  })

  /// Use effect for loading the attached file into a base64 data64 image url, if one exists.
  useEffect(() => {
    if (message.originalSendParams?.file === undefined) {
      return
    }

    let isMounted = true

    const loadFile = async (file: File) => {
      const dataURL = (await fileToBase64(file)) as string
      if (!isMounted) {
        return
      }
      setFileUrl(dataURL)
    }

    loadFile(message.originalSendParams?.file)
    return () => {
      isMounted = false
    }
  })

  const handleRetry = () => {
    if (message.originalSendParams === undefined) {
      return
    }
    onRetry?.(message)
  }

  return (
    <Flex css={styles.container}>
      {isFailed && canRetry ? (
        <Text asChild css={styles.statusText}>
          <button type="button" onClick={handleRetry}>
            {`${t('messageInput.statusMessage.failedToUpload')}. `}
            <Text as="span" css={styles.retryText}>
              {t('messageInput.retry')}
            </Text>
            ?
          </button>
        </Text>
      ) : (
        <Text css={styles.statusText}>
          {isFailed
            ? `${t('messageInput.statusMessage.failedToUpload')}.`
            : `${t('messageInput.statusMessage.uploading')}`}
        </Text>
      )}
      <Box
        css={styles.bubble}
        animation={isFailed ? undefined : GradientBackgroundSendingAnimation}
      >
        <Flex css={styles.messageTextWrapper}>
          {hasBody && <Text css={styles.messageText}>{message.body}</Text>}
          {message.messageType === 'file' &&
            (fileUrl ? (
              <Image
                css={styles.messageImage}
                src={fileUrl}
                alt={message.originalSendParams?.file?.name ?? ''}
              />
            ) : (
              <ImageIcon size={42} color="var(--chakra-colors-fg-muted)" />
            ))}
        </Flex>
      </Box>
    </Flex>
  )
}

export default PendingMessageBubble
